import { createContext, useCallback, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode } from "react";
import { appPath, routerPath } from "./app";
import { sessionController, signInUrl, type RefreshOutcome, type Session, type SessionController, type SessionEndReason } from "./session";
import type { AuthResponse, AuthUser, SessionResponse } from "./types";


interface AuthContextValue {
  session: Session | null;
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  /** Stores a sign-in, refresh or handoff answer. `arrival` marks a fresh sign-in (the last-sign-in message shows once). */
  setSession: (s: AuthResponse | SessionResponse, options?: { arrival?: boolean }) => void;
  /** Replaces the cached user (e.g. after `GET /api/auth/me` returns fresh roles/permissions) without touching the token. */
  updateUser: (user: AuthUser) => void;
  /**
   * Signs out on this device: the server session is revoked (best effort), storage is cleared and the other tabs follow. The caller
   * navigates (most sign-out buttons go to `/login` themselves); `signOut` also navigates.
   */
  clear: () => void;
  /** Ends the session and takes the person to the sign-in page with the reason (`/login?reason=idle`). */
  signOut: (reason?: SessionEndReason) => void;
  /** Renews the access token now (counts as activity). */
  refresh: () => Promise<RefreshOutcome>;
  hasRole: (role: string) => boolean;
  /** True for a session of the strict sign-in (sid, rotating refresh cookie, idle and absolute limits). */
  managed: boolean;
  markActivity: () => void;
  /** Clears a one-shot field once shown (the arrival message, the administrator notices). */
  acknowledge: (field: "arrival" | "notices") => void;
  controller: SessionController;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Creates a token accessor usable before React renders (e.g. for ApiProvider.getToken). */
export function createTokenAccessor(storageKey: string): () => string | null {
  const c = sessionController(storageKey);
  return () => c.getToken();
}

const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart"] as const;
const TICK_MS = 5_000;

export interface AuthProviderProps {
  storageKey: string;
  /** Base URL of the API (`VITE_API_URL`, usually ""), for refresh and sign-out. */
  apiBase?: string;
  /** The BroadcastChannel tabs share (ECONIC uses `econic-session`). */
  channelName?: string;
  /** The app's sign-in path (portal: `/auth/uaepass`). */
  loginPath?: string;
  /**
   * Called when the session ends by itself (idle, absolute limit, revoked, another tab). The default hard-navigates to
   * `loginPath?reason=…&from=…`, which also resets in-memory state; drafts kept by `useDraft` survive on the device.
   */
  onSessionEnded?: (reason: SessionEndReason, from: string) => void;
  children: ReactNode;
}

function readRaw(storageKey: string): string {
  try {
    return typeof window === "undefined" ? "" : (window.localStorage.getItem(storageKey) ?? "");
  } catch {
    return "";
  }
}

/** Where the person is, as a path of this app (the sign-in page returns there through the router). */
function currentPath(): string {
  const { pathname, search, hash } = window.location;
  return `${routerPath(pathname)}${search}${hash}`;
}

export function AuthProvider({ storageKey, apiBase = "", channelName, loginPath = "/login", onSessionEnded, children }: AuthProviderProps) {
  const controller = useMemo(() => sessionController(storageKey, { apiBase, channelName }), [storageKey, apiBase, channelName]);

  // Re-render when the stored session changes (this tab or another); activity has its own store (useSessionClock) so that it does not
  // re-render every consumer of the session.
  const raw = useSyncExternalStore(
    useCallback((cb) => controller.subscribe(cb), [controller]),
    () => readRaw(storageKey),
    () => "",
  );
  const session = useMemo(() => controller.read(), [controller, raw]); // eslint-disable-line react-hooks/exhaustive-deps

  const ended = useCallback(
    (reason: SessionEndReason) => {
      if (reason === "signed_out") return; // the button that signed out navigates
      const from = currentPath();
      if (onSessionEnded) onSessionEnded(reason, from);
      else if (!routerPath(window.location.pathname).startsWith(loginPath)) window.location.assign(appPath(signInUrl(loginPath, reason, from)));
    },
    [onSessionEnded, loginPath],
  );

  useEffect(() => controller.onEnd((reason) => ended(reason)), [controller, ended]);

  const managed = controller.isManaged(session);

  // Activity in this tab (shared with the others through storage). Focus moving into a frame (a desk) counts too: frame events do not
  // bubble to the host, and their own heartbeats arrive through `markSessionActivity()`.
  useEffect(() => {
    if (!managed) return;
    // Input inside a session prompt (the idle dialog) is a choice, not activity: the dialog must not close under the pointer.
    const mark = (e: Event) => {
      const target = e.target as Element | null;
      if (target && typeof target.closest === "function" && target.closest("[data-session-prompt]")) return;
      controller.markActivity();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") controller.markActivity();
    };
    const onBlur = () => {
      if (document.activeElement?.tagName === "IFRAME") controller.markActivity();
    };
    for (const e of ACTIVITY_EVENTS) window.addEventListener(e, mark, { capture: true, passive: true });
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("blur", onBlur);
    if (!controller.lastActivity()) controller.markActivity(true);
    return () => {
      for (const e of ACTIVITY_EVENTS) window.removeEventListener(e, mark, { capture: true });
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("blur", onBlur);
    };
  }, [controller, managed]);

  // The session clock: absolute limit, idle limit, and silent token renewal according to the server session policy.
  useEffect(() => {
    if (!session) return;
    const check = () => {
      const s = controller.read();
      if (!s) return;
      const now = Date.now();
      if (controller.isManaged(s)) {
        const absolute = controller.absoluteDeadline(s);
        if (absolute !== null && now >= absolute) return controller.end("expired", { revoke: false });
        const idle = controller.idleDeadline(s);
        if (idle !== null && now >= idle) return controller.end("idle");
        if (controller.shouldRefresh(s)) void controller.refresh();
      }
    };
    check();
    const timer = window.setInterval(check, TICK_MS);
    const onVisible = () => document.visibilityState === "visible" && check();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [controller, session?.sessionId, session !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  const setSession = useCallback((s: AuthResponse | SessionResponse, options?: { arrival?: boolean }) => void controller.store(s, options), [controller]);

  const updateUser = useCallback(
    (user: AuthUser) => {
      const current = controller.read();
      if (current) controller.write({ ...current, user });
    },
    [controller],
  );

  const clear = useCallback(() => controller.end("signed_out"), [controller]);

  const signOut = useCallback(
    (reason: SessionEndReason = "signed_out") => {
      const from = currentPath();
      controller.end(reason, { revoke: reason === "signed_out" || reason === "idle" });
      if (reason === "signed_out") window.location.assign(appPath(loginPath));
      else if (!onSessionEnded) window.location.assign(appPath(signInUrl(loginPath, reason, from)));
    },
    [controller, loginPath, onSessionEnded],
  );

  const refresh = useCallback(() => {
    controller.markActivity(true);
    return controller.refresh();
  }, [controller]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      token: session?.accessToken ?? null,
      isAuthenticated: !!session,
      setSession,
      updateUser,
      clear,
      signOut,
      refresh,
      hasRole: (role) => session?.user.roles.includes(role) ?? false,
      managed,
      markActivity: () => controller.markActivity(),
      acknowledge: (field) => controller.acknowledge(field),
      controller,
    }),
    [session, setSession, updateUser, clear, signOut, refresh, managed, controller],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** The signed-in user's id, or null. Unlike useAuth it works outside <AuthProvider> (always null there). */
export function useSessionUserId(): string | null {
  return useContext(AuthContext)?.user?.id ?? null;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

/** Like useAuth, but null outside <AuthProvider> (embedded hosts without a session). */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}

export interface SessionClock {
  /** Last activity in any tab of this app (ms epoch; 0 before any). */
  lastActivity: number;
  /** When the session lapses for inactivity (ms epoch); null for sessions without an idle limit. */
  idleDeadline: number | null;
  /** The 10-hour limit (ms epoch); null when the server did not state one. */
  absoluteDeadline: number | null;
  idleMs: number;
}

/** The session's deadlines, updated on activity in any tab (for the idle dialog, the status bar countdown and the absolute notice). */
export function useSessionClock(): SessionClock {
  const { controller, session } = useAuth();
  const last = useSyncExternalStore(
    useCallback((cb) => controller.subscribe(cb), [controller]),
    () => controller.lastActivity(),
    () => 0,
  );
  return useMemo(
    () => ({
      lastActivity: last,
      idleDeadline: controller.idleDeadline(session),
      absoluteDeadline: controller.absoluteDeadline(session),
      idleMs: controller.idleMs(session),
    }),
    [controller, session, last],
  );
}
